// Boundary overlay: draws layer/frame outlines on top of the viewport.
//
// Trick: scene world space == viewport resolution pixels, so an <svg> with
// a scaled content group lets us emit each
// node's quad directly as polygon points — the browser handles the
// world->screen scaling. `vector-effect: non-scaling-stroke` keeps outlines a
// crisp 1px regardless of how the viewport is scaled down to fit.

import type { LayoutMap } from '../scene/layoutEngine.js';
import type { Quad } from '../scene/layoutMath.js';
import type { Scene, SceneNode, SceneNodeId } from '../scene/sceneState.js';
import { VIEWPORT_OVERLAY_STROKE_PAD } from './viewportFrame.js';
import type { ViewportResolution } from './viewportResolution.js';

const SVG_NS = 'http://www.w3.org/2000/svg';

interface OverlayRenderOptions {
  selectedId?: SceneNodeId | null;
}

interface BoundEntry {
  el: SVGPolygonElement;
  guideEl: SVGLineElement | null;
  kind: SceneNode['kind'];
}

export interface ViewportOverlayController {
  /** Draw boundaries for the rendered scene. Aligns itself to the canvas. */
  render(
    resolution: ViewportResolution,
    scene: Scene,
    layoutMap: LayoutMap,
    options?: OverlayRenderOptions,
  ): void;
  /** Update the hovered node without a full re-render (cheap; per-mousemove). */
  setHovered(nodeId: SceneNodeId | null): void;
  /** Remove all boundaries (e.g. for standalone/cleared viewport renders). */
  clear(): void;
}

function quadToPoints(q: Quad): string {
  return `${q.tl[0]},${q.tl[1]} ${q.tr[0]},${q.tr[1]} ${q.br[0]},${q.br[1]} ${q.bl[0]},${q.bl[1]}`;
}

function setLinePoint(line: SVGLineElement, attr: 'x1' | 'y1' | 'x2' | 'y2', value: number): void {
  const next = String(value);
  if (line.getAttribute(attr) !== next) line.setAttribute(attr, next);
}

function frameContainsNode(scene: Scene, frameId: SceneNodeId, targetId: SceneNodeId): boolean {
  const frame = scene.nodes[frameId];
  if (frame?.kind !== 'frame') return false;
  const stack = [...frame.childIds];
  while (stack.length > 0) {
    const nodeId = stack.pop()!;
    if (nodeId === targetId) return true;
    const node = scene.nodes[nodeId];
    if (node?.kind === 'frame') stack.push(...node.childIds);
  }
  return false;
}

function isFrameGuideInSelectionContext(
  scene: Scene,
  nodeId: SceneNodeId,
  selectedNodeId: SceneNodeId | null,
): boolean {
  if (!selectedNodeId) return true;
  if (nodeId === selectedNodeId) return true;
  return frameContainsNode(scene, nodeId, selectedNodeId)
    || frameContainsNode(scene, selectedNodeId, nodeId);
}

function syncFrameGuide(
  group: SVGGElement,
  entry: BoundEntry,
  scene: Scene,
  nodeId: SceneNodeId,
  node: SceneNode,
  quad: Quad,
  selectedNodeId: SceneNodeId | null,
): void {
  const guideKind = node.kind === 'frame'
    && (node.layout.kind === 'row' || node.layout.kind === 'column')
    ? node.layout.kind
    : null;
  if (!guideKind) {
    entry.guideEl?.remove();
    entry.guideEl = null;
    return;
  }

  if (!entry.guideEl) {
    entry.guideEl = document.createElementNS(SVG_NS, 'line');
    entry.guideEl.setAttribute('vector-effect', 'non-scaling-stroke');
    group.appendChild(entry.guideEl);
  }

  const guide = entry.guideEl;
  const contextual = isFrameGuideInSelectionContext(scene, nodeId, selectedNodeId);
  const cls = `viewport-frame-guide viewport-frame-guide--${guideKind}${contextual ? '' : ' is-out-of-context'}`;
  if (guide.getAttribute('class') !== cls) guide.setAttribute('class', cls);

  if (guideKind === 'row') {
    setLinePoint(guide, 'x1', (quad.tl[0] + quad.bl[0]) * 0.5);
    setLinePoint(guide, 'y1', (quad.tl[1] + quad.bl[1]) * 0.5);
    setLinePoint(guide, 'x2', (quad.tr[0] + quad.br[0]) * 0.5);
    setLinePoint(guide, 'y2', (quad.tr[1] + quad.br[1]) * 0.5);
  } else {
    setLinePoint(guide, 'x1', (quad.tl[0] + quad.tr[0]) * 0.5);
    setLinePoint(guide, 'y1', (quad.tl[1] + quad.tr[1]) * 0.5);
    setLinePoint(guide, 'x2', (quad.bl[0] + quad.br[0]) * 0.5);
    setLinePoint(guide, 'y2', (quad.bl[1] + quad.br[1]) * 0.5);
  }
}

export function createViewportOverlayController(): ViewportOverlayController {
  let svg: SVGSVGElement | null = null;
  let contentGroup: SVGGElement | null = null;
  const polyCache = new Map<SceneNodeId, BoundEntry>();
  let selectedId: SceneNodeId | null = null;
  let hoveredId: SceneNodeId | null = null;

  function ensureSvg(): SVGSVGElement | null {
    if (svg && svg.isConnected) return svg;
    const wrap = document.getElementById('viewport-canvas-wrap');
    if (!wrap) return null;
    let el = wrap.querySelector<SVGSVGElement>('#viewport-overlay');
    if (!el) {
      el = document.createElementNS(SVG_NS, 'svg');
      el.id = 'viewport-overlay';
      el.setAttribute('class', 'viewport-overlay');
      el.setAttribute('aria-hidden', 'true');
      wrap.appendChild(el);
    }
    let group = el.querySelector<SVGGElement>('#viewport-overlay-content');
    if (!group) {
      group = document.createElementNS(SVG_NS, 'g');
      group.id = 'viewport-overlay-content';
      el.appendChild(group);
    }
    svg = el;
    contentGroup = group;
    return el;
  }

  function syncFrame(el: SVGSVGElement, resolution: ViewportResolution): boolean {
    const canvas = document.getElementById('viewport-canvas') as HTMLCanvasElement | null;
    if (!canvas) return false;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (w <= 0 || h <= 0) return false;
    const scaleX = w / resolution.w;
    const scaleY = h / resolution.h;
    const scale = Math.max(0.0001, Math.min(scaleX, scaleY));
    const cssPad = VIEWPORT_OVERLAY_STROKE_PAD;
    el.setAttribute('viewBox', `0 0 ${w + cssPad * 2} ${h + cssPad * 2}`);
    el.style.left = `${canvas.offsetLeft - cssPad}px`;
    el.style.top = `${canvas.offsetTop - cssPad}px`;
    el.style.width = `${w + cssPad * 2}px`;
    el.style.height = `${h + cssPad * 2}px`;
    contentGroup?.setAttribute('transform', `translate(${cssPad} ${cssPad}) scale(${scale})`);
    return true;
  }

  function classFor(nodeId: SceneNodeId, kind: SceneNode['kind']): string {
    let cls = `viewport-boundary viewport-boundary--${kind}`;
    if (nodeId === selectedId) cls += ' is-selected';
    if (nodeId === hoveredId) cls += ' is-hovered';
    return cls;
  }

  function applyClass(nodeId: SceneNodeId): void {
    const entry = polyCache.get(nodeId);
    if (!entry) return;
    const cls = classFor(nodeId, entry.kind);
    if (entry.el.getAttribute('class') !== cls) entry.el.setAttribute('class', cls);
  }

  function render(
    resolution: ViewportResolution,
    scene: Scene,
    layoutMap: LayoutMap,
    options: OverlayRenderOptions = {},
  ): void {
    const el = ensureSvg();
    if (!el) return;
    if (!syncFrame(el, resolution)) {
      clear();
      return;
    }
    const group = contentGroup;
    if (!group) return;
    el.style.display = '';

    selectedId = options.selectedId ?? null;
    const active = new Set<SceneNodeId>();

    for (const nodeId of Object.keys(layoutMap)) {
      const node = scene.nodes[nodeId];
      if (!node || !node.visible) continue;
      const layout = layoutMap[nodeId];
      if (!layout) continue;
      active.add(nodeId);

      let entry = polyCache.get(nodeId);
      if (!entry) {
        const poly = document.createElementNS(SVG_NS, 'polygon');
        poly.setAttribute('vector-effect', 'non-scaling-stroke');
        group.appendChild(poly);
        entry = { el: poly, guideEl: null, kind: node.kind };
        polyCache.set(nodeId, entry);
      } else {
        entry.kind = node.kind;
      }

      const cls = classFor(nodeId, node.kind);
      if (entry.el.getAttribute('class') !== cls) entry.el.setAttribute('class', cls);
      const points = quadToPoints(layout.quad);
      if (entry.el.getAttribute('points') !== points) entry.el.setAttribute('points', points);
      syncFrameGuide(group, entry, scene, nodeId, node, layout.quad, selectedId);
    }

    for (const [nodeId, entry] of polyCache) {
      if (active.has(nodeId)) continue;
      entry.el.remove();
      entry.guideEl?.remove();
      polyCache.delete(nodeId);
    }
    if (hoveredId && !polyCache.has(hoveredId)) hoveredId = null;

    // Keep the selected outline on top for visibility.
    if (selectedId) {
      const sel = polyCache.get(selectedId);
      if (sel) {
        if (sel.guideEl?.parentNode === group) group.appendChild(sel.guideEl);
        if (sel.el.parentNode === group) group.appendChild(sel.el);
      }
    }
  }

  function setHovered(nodeId: SceneNodeId | null): void {
    const next = nodeId && polyCache.has(nodeId) ? nodeId : null;
    if (next === hoveredId) return;
    const prev = hoveredId;
    hoveredId = next;
    if (prev) applyClass(prev);
    if (next) applyClass(next);
  }

  function clear(): void {
    for (const entry of polyCache.values()) {
      entry.el.remove();
      entry.guideEl?.remove();
    }
    polyCache.clear();
    selectedId = null;
    hoveredId = null;
    if (svg) svg.style.display = 'none';
  }

  return { render, setHovered, clear };
}
